import { Button } from '@design-system/Button';
import { Card, CardContent } from '@design-system/Card';
import type { SMPLacoreOrder } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { getProductImageForSMP } from '../utils/smpHelpers';
import { ShimmerImage } from './ShimmerImage';

export const ORDER_STATE_PROGRESS: Record<string, number> = {
  Placed: 5,
  Processing: 35,
  Shipped: 70,
  Delivered: 100,
};

export const ORDER_STATE_TEXT: Record<string, string> = {
  Placed: 'Your order has been placed.',
  Processing: "We've started manufacturing your order.",
  Shipped: 'Your order is on the way!',
  Delivered: 'Your order is delivered.',
};

export const getOrderStatusText = (lacoreOrder: SMPLacoreOrder): string =>
  ORDER_STATE_TEXT[lacoreOrder.current_state] ?? lacoreOrder.status_text ?? '';

const OIPProgressBar = ({ lacoreOrder }: { lacoreOrder: SMPLacoreOrder }) => {
  const pct = ORDER_STATE_PROGRESS[lacoreOrder.current_state] ?? 0;
  return (
    <div className="flex flex-col gap-3">
      <div className="relative w-full py-2">
        <div className="w-full h-1 rounded-full bg-stroke-secondary-subtle" />
        <div
          className="absolute top-1/2 -translate-y-1/2 left-0 h-1 rounded-full bg-success transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between">
        {lacoreOrder.available_states.map((s) => (
          <span key={s} className="typography-caption2 text-secondary">{s}</span>
        ))}
      </div>
    </div>
  );
};

export const OIPCard = ({
  lacoreOrder,
  planName,
  productImage,
  footerText,
}: {
  lacoreOrder: SMPLacoreOrder;
  planName?: string;
  productImage?: string;
  footerText?: string;
}) => (
  <Card>
    <CardContent className="flex flex-col gap-6 py-6">
      <span className="typography-h3 text-primary">{getOrderStatusText(lacoreOrder)}</span>
      <OIPProgressBar lacoreOrder={lacoreOrder} />
      {planName && (
        <div className="flex items-center gap-5">
          {productImage && (
            <ShimmerImage src={getProductImageForSMP(planName) || productImage} alt={planName} containerClassName="size-[72px] rounded-[12px] overflow-hidden flex items-center justify-center shrink-0" className="size-full object-cover" />
          )}
          <span className="typography-body text-primary">{planName}</span>
        </div>
      )}
      {footerText && <p className="typography-caption1 text-secondary">{footerText}</p>}
    </CardContent>
  </Card>
);

export const OrderInProgressDisclaimer = ({
  lacoreOrder,
  planName,
  productImage,
  onContinue,
  onCancel,
  continueLabel = 'Yes',
}: {
  lacoreOrder: SMPLacoreOrder;
  planName?: string;
  productImage?: string;
  onContinue: () => void;
  onCancel: () => void;
  continueLabel?: string;
}) => (
  <>
    <OIPCard lacoreOrder={lacoreOrder} planName={planName} productImage={productImage} />
    <p className="typography-body text-primary max-w-xl">
      You currently have an order in progress. Any changes you make will not
      affect this order and it will be delivered as scheduled. Do you wish to continue?
    </p>
    <div className="flex flex-col gap-4 w-full max-w-sm">
      <Button onClick={onContinue}>{continueLabel}</Button>
      <Button variant="secondary" onClick={onCancel}>Cancel</Button>
    </div>
  </>
);
